import { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { apiClient } from '../services/api'
import { User } from '../types'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  login: (_email: string, _password: string, _totpCode?: string) => Promise<void>
  webauthnLogin: (_mfaPendingToken: string, _assertion: any) => Promise<void>
  register: (_userData: {
    email: string
    password: string
    name: string
    organizationName: string
  }) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const initAuth = async () => {
      try {
        const response = await apiClient.getCurrentUser()
        setUser(response.data.user)
      } catch (_error) {
        // No valid session — user stays null
      }
      setIsLoading(false)
    }

    initAuth()
  }, [])

  const login = async (email: string, password: string, totpCode?: string) => {
    const response = await apiClient.login({ email, password, ...(totpCode ? { totpCode } : {}) })
    setUser(response.data.user)
  }

  /** Complete a WebAuthn/FIDO2 login ceremony started from the MFA_REQUIRED step. */
  const webauthnLogin = async (mfaPendingToken: string, assertion: any) => {
    const response = await apiClient.webauthnLoginVerify(mfaPendingToken, assertion)
    setUser(response.data.user)
  }

  const register = async (userData: {
    email: string
    password: string
    name: string
    organizationName: string
  }) => {
    const response = await apiClient.register(userData)
    setUser(response.data.user)
  }

  const logout = async () => {
    try {
      await apiClient.logout()
    } catch (_error) {
      // Ignore logout errors
    } finally {
      setUser(null)
    }
  }

  const refreshUser = async () => {
    try {
      const response = await apiClient.getCurrentUser()
      setUser(response.data.user)
    } catch (_error) {
      // leave the current user as-is on a transient failure
    }
  }

  const value = {
    user,
    isLoading,
    login,
    webauthnLogin,
    register,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    // Return default "no user" state when outside AuthProvider (e.g., during prerendering)
    return {
      user: null,
      isLoading: false,
      login: async () => {},
      webauthnLogin: async () => {},
      register: async () => {},
      logout: () => {},
      refreshUser: async () => {},
    }
  }
  return context
}
